// Anthropic Claude Provider
// Premium tier AI for high-value tasks

import { AIService } from './interface';
import { Quote, LaborRole, LaborStep, MaterialLine, PriceListItem, AIBrief, AIActionProposal, SocialMediaDraft, RFQDraft, ProposalSettings, OutreachDraft, Customer, CompanyInfo, LeadScore, CaseStudy, ProjectFinancialHealth, Invoice, MaintenanceChecklist, ServiceReport } from '../../types';
import { callAIProxy, isAIProxyAvailable } from './proxyClient';

const DEFAULT_CLAUDE_MODEL = 'claude-sonnet-4-20250514';

// Cache control types
type CacheTTL = '5m' | '1h';

interface CacheControl {
  type: 'ephemeral';
  ttl?: CacheTTL;
}

interface CacheOptions {
  enabled?: boolean;
  systemPromptTTL?: CacheTTL;
  contextTTL?: CacheTTL;
  minContextSizeForCache?: number; // bytes threshold
}

// Default cache options
const DEFAULT_CACHE_OPTIONS: Required<CacheOptions> = {
  enabled: true,
  systemPromptTTL: '5m',
  contextTTL: '1h',
  minContextSizeForCache: 50 * 1024, // 50KB
};

// Helper to create cache control
function createCacheControl(ttl: CacheTTL = '5m'): CacheControl {
  return { type: 'ephemeral', ttl };
}

// Helper to convert string system prompt to array format with cache control
function systemPromptToArray(systemPrompt: string, ttl: CacheTTL = '5m'): Array<{ type: 'text'; text: string; cache_control: CacheControl }> {
  return [{
    type: 'text' as const,
    text: systemPrompt,
    cache_control: createCacheControl(ttl),
  }];
}

// Helper to determine if content should be cached based on size
function shouldCacheContent(content: string, minSize: number = DEFAULT_CACHE_OPTIONS.minContextSizeForCache): boolean {
  return new Blob([content]).size >= minSize;
}

// Helper to estimate base64 size in bytes
function estimateBase64Size(base64: string): number {
  // Base64 encoding is ~4/3 the size of original
  // Remove data URL prefix if present
  const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
  return Math.floor((base64Data.length * 3) / 4);
}

export class AnthropicProvider implements AIService {
  private model: string = DEFAULT_CLAUDE_MODEL;
  
  /**
   * Set the model to use for API calls
   */
  setModel(model: string): void {
    this.model = model;
  }
  
  /**
   * Get the current model
   */
  getModel(): string {
    return this.model;
  }
  
  private async callClaude(
    systemPrompt: string | Array<{ type: 'text'; text: string; cache_control?: CacheControl }>,
    userMessage: string | Array<{ type: 'text'; text: string; cache_control?: CacheControl }>,
    options: { maxTokens?: number; temperature?: number; cacheOptions?: CacheOptions } = {}
  ): Promise<string> {
    const { maxTokens = 4096, temperature = 0.7, cacheOptions = {} } = options;
    const cacheConfig = { ...DEFAULT_CACHE_OPTIONS, ...cacheOptions };

    // Convert system prompt to array format with cache control if it's a string
    let systemArray: Array<{ type: 'text'; text: string; cache_control?: CacheControl }>;
    if (typeof systemPrompt === 'string') {
      systemArray = cacheConfig.enabled 
        ? systemPromptToArray(systemPrompt, cacheConfig.systemPromptTTL)
        : [{ type: 'text', text: systemPrompt }];
    } else {
      systemArray = systemPrompt;
    }

    // Convert user message to array format if needed
    let userMessageArray: Array<{ type: 'text'; text: string; cache_control?: CacheControl }>;
    if (typeof userMessage === 'string') {
      userMessageArray = [{ type: 'text', text: userMessage }];
    } else {
      userMessageArray = userMessage;
    }

    // Prepare messages array
    const messages = [{ role: 'user' as const, content: userMessageArray }];

    if (!isAIProxyAvailable()) {
      throw new Error('AI proxy not configured. Set VITE_AI_WORKER_URL and Workers secrets.');
    }

    try {
      const response = await callAIProxy('anthropic' as any, 'messages', {
        model: this.model,
        max_tokens: maxTokens,
        temperature,
        system: systemArray,
        messages: messages,
      });

      if (response?.content?.[0]?.text) {
        return response.content[0].text;
      }

      // If proxy returned but no content, log for debugging
      if (response) {
        console.warn('Claude proxy returned unexpected format:', response);
      }
    } catch (error) {
      console.error('Claude proxy call failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('invalid x-api-key') || errorMessage.includes('401')) {
        throw new Error('Anthropic API key is invalid or expired. Please update it using: npx wrangler pages secret put ANTHROPIC_API_KEY');
      }
      throw new Error(`AI proxy failed: ${errorMessage}. Please check Workers secrets and function deployment.`);
    }

    throw new Error('AI proxy returned an empty response.');
  }

  private async callClaudeJSON<T>(
    systemPrompt: string | Array<{ type: 'text'; text: string; cache_control?: CacheControl }>,
    userMessage: string | Array<{ type: 'text'; text: string; cache_control?: CacheControl }>,
    options: { maxTokens?: number; temperature?: number; cacheOptions?: CacheOptions } = {}
  ): Promise<T> {
    // Add JSON instruction to system prompt
    let jsonSystemPrompt: string | Array<{ type: 'text'; text: string; cache_control?: CacheControl }>;
    if (typeof systemPrompt === 'string') {
      jsonSystemPrompt = `${systemPrompt}\n\nIMPORTANT: You MUST respond with valid JSON only. No markdown, no explanation, just the JSON object.`;
    } else {
      // If system prompt is already an array, add JSON instruction to the last block
      const lastBlock = systemPrompt[systemPrompt.length - 1];
      jsonSystemPrompt = [
        ...systemPrompt.slice(0, -1),
        {
          ...lastBlock,
          text: `${lastBlock.text}\n\nIMPORTANT: You MUST respond with valid JSON only. No markdown, no explanation, just the JSON object.`,
        },
      ];
    }
    
    const result = await this.callClaude(jsonSystemPrompt, userMessage, options);
    
    // Extract JSON from response (handle markdown code blocks)
    let jsonStr = result.trim();
    if (jsonStr.startsWith('```json')) {
      jsonStr = jsonStr.replace(/^```json\s*/, '').replace(/\s*```$/, '');
    } else if (jsonStr.startsWith('```')) {
      jsonStr = jsonStr.replace(/^```\s*/, '').replace(/\s*```$/, '');
    }
    
    return JSON.parse(jsonStr);
  }

  // ============================================================================
  // PREMIUM FEATURES - Claude excels at these
  // ============================================================================

  async analyzeWinLossPatterns(quotes: Quote[]): Promise<{
    patterns: string[];
    recommendations: string[];
    riskFactors: string[];
  }> {
    const wonQuotes = quotes.filter(q => q.status === 'WON');
    const lostQuotes = quotes.filter(q => q.status === 'LOST');

    const systemPrompt = `You are a business analyst specializing in sales optimization for construction/electrical contractors. Analyze win/loss patterns to provide actionable insights.`;

    const userMessage = `Analyze these quotes to identify patterns:

WON QUOTES (${wonQuotes.length}):
${wonQuotes.slice(0, 10).map(q => `- ${q.projectName}: $${q.totalPrice}, ${q.lossReason || 'No notes'}`).join('\n')}

LOST QUOTES (${lostQuotes.length}):
${lostQuotes.slice(0, 10).map(q => `- ${q.projectName}: $${q.totalPrice}, Reason: ${q.lossReason || 'Unknown'}`).join('\n')}

Provide analysis as JSON:
{
  "patterns": ["pattern1", "pattern2"],
  "recommendations": ["rec1", "rec2"],
  "riskFactors": ["risk1", "risk2"]
}`;

    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async generatePricingRecommendation(
    quote: Quote,
    historicalQuotes: Quote[],
    customerHistory: Quote[]
  ): Promise<{
    recommendedPrice: number;
    confidence: number;
    reasoning: string;
    adjustments: { factor: string; impact: number }[];
  }> {
    const systemPrompt = `You are a pricing strategist for construction/electrical contractors. Analyze historical data to recommend optimal pricing that balances win probability with profitability.`;

    const similarQuotes = historicalQuotes
      .filter(q => q.status === 'WON' || q.status === 'LOST')
      .slice(0, 20);

    const userMessage = `Recommend pricing for this quote:

CURRENT QUOTE:
- Project: ${quote.projectName}
- Customer: ${quote.clientName}
- Calculated Price: $${quote.totalPrice}
- Total Hours: ${quote.totalManHours}
- Scope: ${quote.bidItems.map(i => i.title).join(', ')}

SIMILAR HISTORICAL QUOTES:
${similarQuotes.map(q => `- ${q.projectName}: $${q.totalPrice}, ${q.status}`).join('\n')}

CUSTOMER HISTORY (${customerHistory.length} previous quotes):
${customerHistory.map(q => `- ${q.projectName}: $${q.totalPrice}, ${q.status}`).join('\n')}

Provide recommendation as JSON:
{
  "recommendedPrice": 12500,
  "confidence": 0.75,
  "reasoning": "explanation here",
  "adjustments": [
    {"factor": "Repeat customer discount", "impact": -500},
    {"factor": "Market rate adjustment", "impact": 200}
  ]
}`;

    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async generateStrategicInsights(
    metrics: { revenue: number; winRate: number; avgMargin: number },
    recentActivity: string
  ): Promise<{
    insights: string[];
    opportunities: string[];
    warnings: string[];
    actionItems: { action: string; priority: 'HIGH' | 'MEDIUM' | 'LOW' }[];
  }> {
    const systemPrompt = `You are a business strategist for a small construction/electrical contracting company. Provide actionable strategic insights based on business metrics and recent activity.`;

    const userMessage = `Analyze this business data and provide strategic insights:

METRICS:
- Revenue: $${metrics.revenue}
- Win Rate: ${metrics.winRate}%
- Avg Margin: ${metrics.avgMargin}%

RECENT ACTIVITY:
${recentActivity}

Provide insights as JSON:
{
  "insights": ["insight1", "insight2"],
  "opportunities": ["opp1", "opp2"],
  "warnings": ["warning1"],
  "actionItems": [
    {"action": "action description", "priority": "HIGH"}
  ]
}`;

    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  // ============================================================================
  // STANDARD AI SERVICE INTERFACE IMPLEMENTATIONS
  // ============================================================================

  async generateScopeSuggestions(context: string, existingRoles: LaborRole[]): Promise<any> {
    const systemPrompt = `You are an expert electrical/construction estimator. Generate scope suggestions.`;
    const userMessage = `Generate scope for: ${context}\n\nAvailable roles: ${existingRoles.map(r => r.name).join(', ')}\n\nRespond with JSON: { "title": "", "notes": "", "laborSteps": [], "suggestedMaterials": [] }`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async generateScopeSuggestionsFromDocument(base64Data: string, mimeType: string, existingRoles: LaborRole[]): Promise<any> {
    // Claude can handle images via their vision API
    const systemPrompt = `You are an expert construction estimator. Analyze this document and extract scope of work.`;
    
    const docSize = estimateBase64Size(base64Data);
    const shouldCacheDoc = docSize > 100 * 1024; // 100KB threshold
    
    const userMessageBlocks: Array<{ type: 'text'; text: string; cache_control?: CacheControl }> = [
      {
        type: 'text',
        text: `Analyze this document (${mimeType}, ${Math.round(docSize / 1024)}KB) and extract scope of work.\n\nAvailable roles: ${existingRoles.map(r => r.name).join(', ')}\n\nRespond with JSON.\n\nDocument:`,
      },
      {
        type: 'text',
        text: base64Data,
        cache_control: shouldCacheDoc ? createCacheControl('1h') : undefined,
      },
    ];
    
    return this.callClaudeJSON(systemPrompt, userMessageBlocks, {
      cacheOptions: { enabled: true, systemPromptTTL: '5m' },
    });
  }

  async suggestMaterialLines(title: string, scopeNotes: string, laborSteps: LaborStep[], currentMaterials: MaterialLine[], priceList: PriceListItem[]): Promise<any[]> {
    const systemPrompt = `You are a construction material estimator. Suggest materials based on scope.`;
    const userMessage = `Suggest materials for:\nTitle: ${title}\nScope: ${scopeNotes}\nLabor: ${laborSteps.map(s => s.description).join(', ')}\n\nPrice list items: ${priceList.slice(0, 50).map(p => p.name).join(', ')}\n\nRespond with JSON array of materials.`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async generateLaborSteps(itemTitle: string, itemDescription: string, existingRoles: LaborRole[]): Promise<any> {
    const systemPrompt = `You are a construction labor estimator. Generate labor steps.`;
    const userMessage = `Generate labor steps for:\nTitle: ${itemTitle}\nDescription: ${itemDescription}\n\nRoles: ${existingRoles.map(r => `${r.name} ($${r.rate}/hr)`).join(', ')}\n\nRespond with JSON array of steps.`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async generateScopeFromLabor(title: string, laborSteps: LaborStep[], materialLines: MaterialLine[], existingRoles: LaborRole[], style: 'BULLET' | 'BRIEF' | 'DETAILED'): Promise<string> {
    // Get style instructions from WritingStyleService
    let styleInstructions = '';
    try {
      const { WritingStyleService } = await import('../prompts/writingStyleService');
      styleInstructions = await WritingStyleService.getInstructionsFromLegacyName(style, 'SCOPE_WRITING');
    } catch (e) {
      // Fallback to simple instructions if style service not available
      const fallbacks: Record<string, string> = {
        BRIEF: 'Write a brief, concise description.',
        BULLET: 'Write as a bullet-point list.',
        DETAILED: 'Write a detailed, comprehensive description.',
      };
      styleInstructions = fallbacks[style] || fallbacks.BRIEF;
    }

    const systemPrompt = `You are a technical writer for construction proposals. Write clear, professional scope descriptions.`;
    const laborDescriptions = laborSteps.map(s => s.description).filter(Boolean).join(', ');
    const materialDescriptions = materialLines.map(m => m.description).filter(Boolean).join(', ');
    const userMessage = `Write a scope description for the following work item:

Title: ${title}
${laborDescriptions ? `Labor Steps: ${laborDescriptions}` : ''}
${materialDescriptions ? `Materials: ${materialDescriptions}` : ''}

Style Instructions: ${styleInstructions}

Return plain text only, no markdown formatting.`;
    return this.callClaude(systemPrompt, userMessage);
  }

  async checkScopeConsistency(title: string, scopeNotes: string, laborSteps: LaborStep[], materialLines: MaterialLine[], existingRoles: LaborRole[]): Promise<any[]> {
    const systemPrompt = `You are a QA reviewer for construction estimates. Check for inconsistencies.`;
    const userMessage = `Check consistency:\nTitle: ${title}\nScope: ${scopeNotes}\nLabor: ${laborSteps.map(s => s.description).join(', ')}\nMaterials: ${materialLines.map(m => m.description).join(', ')}\n\nRespond with JSON array of issues.`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async parseBidFormDocument(base64Data: string, mimeType: string): Promise<any> {
    const systemPrompt = `You are a document parser. Extract bid form data.`;
    
    // Estimate document size and cache with 1h TTL if large (>100KB)
    const docSize = estimateBase64Size(base64Data);
    const shouldCacheDoc = docSize > 100 * 1024; // 100KB threshold
    
    const userMessageBlocks: Array<{ type: 'text'; text: string; cache_control?: CacheControl }> = [
      {
        type: 'text',
        text: `Parse this bid form document and extract key information. Respond with JSON.\n\nDocument (${mimeType}, ${Math.round(docSize / 1024)}KB):`,
      },
      {
        type: 'text',
        text: base64Data,
        // Cache large documents with 1h TTL for extended work sessions
        cache_control: shouldCacheDoc ? createCacheControl('1h') : undefined,
      },
    ];
    
    return this.callClaudeJSON(systemPrompt, userMessageBlocks, {
      cacheOptions: { enabled: true, systemPromptTTL: '5m' },
    });
  }

  async parseVendorQuote(base64Data: string, mimeType: string): Promise<any> {
    const systemPrompt = `You are a document parser. Extract vendor quote data.`;
    
    const docSize = estimateBase64Size(base64Data);
    const shouldCacheDoc = docSize > 100 * 1024; // 100KB threshold
    
    const userMessageBlocks: Array<{ type: 'text'; text: string; cache_control?: CacheControl }> = [
      {
        type: 'text',
        text: `Parse this vendor quote and extract line items, prices, and terms. Respond with JSON.\n\nDocument (${mimeType}, ${Math.round(docSize / 1024)}KB):`,
      },
      {
        type: 'text',
        text: base64Data,
        cache_control: shouldCacheDoc ? createCacheControl('1h') : undefined,
      },
    ];
    
    return this.callClaudeJSON(systemPrompt, userMessageBlocks, {
      cacheOptions: { enabled: true, systemPromptTTL: '5m' },
    });
  }

  async parseSupplierInvoice(base64Data: string, mimeType: string): Promise<any> {
    const systemPrompt = `You are a document parser. Extract invoice data.`;
    
    const docSize = estimateBase64Size(base64Data);
    const shouldCacheDoc = docSize > 100 * 1024; // 100KB threshold
    
    const userMessageBlocks: Array<{ type: 'text'; text: string; cache_control?: CacheControl }> = [
      {
        type: 'text',
        text: `Parse this supplier invoice and extract all line items and totals. Respond with JSON.\n\nDocument (${mimeType}, ${Math.round(docSize / 1024)}KB):`,
      },
      {
        type: 'text',
        text: base64Data,
        cache_control: shouldCacheDoc ? createCacheControl('1h') : undefined,
      },
    ];
    
    return this.callClaudeJSON(systemPrompt, userMessageBlocks, {
      cacheOptions: { enabled: true, systemPromptTTL: '5m' },
    });
  }

  async parsePriceListDocument(base64Data: string, mimeType: string): Promise<any> {
    const systemPrompt = `You are a document parser. Extract price list data.`;
    
    const docSize = estimateBase64Size(base64Data);
    const shouldCacheDoc = docSize > 100 * 1024; // 100KB threshold
    
    const userMessageBlocks: Array<{ type: 'text'; text: string; cache_control?: CacheControl }> = [
      {
        type: 'text',
        text: `Parse this price list and extract all items with prices. Respond with JSON array.\n\nDocument (${mimeType}, ${Math.round(docSize / 1024)}KB):`,
      },
      {
        type: 'text',
        text: base64Data,
        cache_control: shouldCacheDoc ? createCacheControl('1h') : undefined,
      },
    ];
    
    return this.callClaudeJSON(systemPrompt, userMessageBlocks, {
      cacheOptions: { enabled: true, systemPromptTTL: '5m' },
    });
  }

  async analyzeQuoteRisks(quote: Quote): Promise<any> {
    const systemPrompt = `You are a risk analyst for construction projects. Identify potential risks.`;
    const userMessage = `Analyze risks for:\nProject: ${quote.projectName}\nValue: $${quote.totalPrice}\nScope: ${quote.bidItems.map(i => i.title).join(', ')}\n\nRespond with JSON: { "risks": [], "mitigations": [] }`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async generateMorningBrief(context: string): Promise<AIBrief> {
    const systemPrompt = `You are a business assistant for a construction/electrical contractor. Generate a comprehensive morning briefing that helps prioritize the day's work.`;
    const userMessage = `Generate a morning brief based on this context: ${context}

Respond with a JSON object matching this exact structure:
{
  "date": "ISO date string",
  "topPriorities": ["priority 1", "priority 2", "priority 3"],
  "upcomingDeadlines": [
    {"title": "Deadline name", "date": "YYYY-MM-DD", "daysLeft": 5}
  ],
  "risks": [
    {"severity": "HIGH|MEDIUM|LOW", "message": "Risk description"}
  ],
  "suggestedSchedule": [
    {"time": "9:00 AM", "task": "Task description"}
  ],
  "quickWins": ["Quick win 1", "Quick win 2"],
  "followUps": [
    {"contact": "Contact name", "context": "Why to follow up", "draftMessage": "Suggested message"}
  ],
  "healthNudges": ["Health reminder 1", "Health reminder 2"]
}

IMPORTANT: Return ONLY valid JSON, no markdown, no explanation.`;
    const result = await this.callClaudeJSON<AIBrief>(systemPrompt, userMessage);
    
    // Ensure all required fields exist with defaults
    return {
      date: result?.date || new Date().toISOString(),
      topPriorities: Array.isArray(result?.topPriorities) ? result.topPriorities : [],
      upcomingDeadlines: Array.isArray(result?.upcomingDeadlines) ? result.upcomingDeadlines : [],
      risks: Array.isArray(result?.risks) ? result.risks : [],
      suggestedSchedule: Array.isArray(result?.suggestedSchedule) ? result.suggestedSchedule : [],
      quickWins: Array.isArray(result?.quickWins) ? result.quickWins : [],
      followUps: Array.isArray(result?.followUps) ? result.followUps : [],
      healthNudges: Array.isArray(result?.healthNudges) ? result.healthNudges : [],
    };
  }

  async analyzeDailyCheckIn(notes: string, context: string): Promise<AIActionProposal[]> {
    const systemPrompt = `You are a business assistant. Extract action items from daily notes.`;
    const userMessage = `Extract actions from: ${notes}\nContext: ${context}\n\nRespond with JSON array of actions.`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async parseDailyLog(logText: string, context: string): Promise<any> {
    const systemPrompt = `You are a time tracking assistant. Parse daily log entries.`;
    const userMessage = `Parse this daily log: ${logText}\nContext: ${context}\n\nExtract time entries, notes, and action items. Respond with JSON.`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async chatWithSecretary(history: { role: 'user' | 'assistant'; content: string }[], context: string, newMessage: string): Promise<string> {
    // Parse context to extract structured data
    let contextData: any = {};
    try {
      contextData = JSON.parse(context);
    } catch (e) {
      // If context is not JSON, use as-is
    }
    
    // Build system prompt blocks with caching
    const systemBlocks: Array<{ type: 'text'; text: string; cache_control?: CacheControl }> = [];
    
    // Base instructions (always cached with 5m TTL)
    const baseInstructions = `You are a helpful business assistant for a construction/electrical contractor. You help with quotes, invoices, schedules, and general business questions.\n\n`;
    systemBlocks.push({
      type: 'text',
      text: baseInstructions,
      cache_control: createCacheControl('5m'),
    });
    
    // Quotes context block (cache with 1h TTL if large enough)
    if (contextData.quotes && Array.isArray(contextData.quotes)) {
      let quotesContext = `CURRENT QUOTES AND PROJECTS (including drafts):\n`;
      contextData.quotes.forEach((q: any) => {
        const statusLabel = q.status === 'DRAFT' ? 'DRAFT' : q.status;
        quotesContext += `- ${q.clientName || 'Unknown Client'}: "${q.projectName || 'Untitled Project'}" (Status: ${statusLabel}, Value: $${q.value?.toLocaleString() || '0'}, ID: ${q.id})\n`;
      });
      quotesContext += `\nWhen asked about a quote, search by client name (e.g., "Trickle Creek") or project name. Quote statuses include: DRAFT, SENT, WON, LOST, OPEN, PENDING, etc. Include ALL quotes including drafts when answering questions.\n\n`;
      
      // Cache quotes context with 1h TTL if it's large enough
      const shouldCacheQuotes = shouldCacheContent(quotesContext, DEFAULT_CACHE_OPTIONS.minContextSizeForCache);
      systemBlocks.push({
        type: 'text',
        text: quotesContext,
        cache_control: shouldCacheQuotes ? createCacheControl('1h') : undefined,
      });
    }
    
    // Customers context block (cache with 1h TTL if large enough)
    if (contextData.customers && Array.isArray(contextData.customers)) {
      let customersContext = `CUSTOMERS:\n`;
      contextData.customers.slice(0, 20).forEach((c: any) => {
        customersContext += `- ${c.companyName || 'Unknown'} (Contact: ${c.contactName || 'N/A'})\n`;
      });
      customersContext += `\n`;
      
      const shouldCacheCustomers = shouldCacheContent(customersContext, DEFAULT_CACHE_OPTIONS.minContextSizeForCache);
      systemBlocks.push({
        type: 'text',
        text: customersContext,
        cache_control: shouldCacheCustomers ? createCacheControl('1h') : undefined,
      });
    }
    
    // Dynamic context (date, company name) - no caching as it changes
    const dynamicContext = `Today's date: ${contextData.date || new Date().toISOString().split('T')[0]}\n`;
    const companyContext = `Company: ${contextData.company?.name || 'Unknown'}\n\n`;
    const finalInstructions = `Answer questions accurately based on the quote and customer data provided above. If you cannot find specific information, say so clearly.`;
    
    systemBlocks.push({
      type: 'text',
      text: dynamicContext + companyContext + finalInstructions,
    });
    
    // Convert history to Anthropic message format with incremental caching
    const messages: Array<{ role: 'user' | 'assistant'; content: Array<{ type: 'text'; text: string; cache_control?: CacheControl }> }> = [];
    
    history.forEach((h, index) => {
      const isLastHistoryMessage = index === history.length - 1;
      messages.push({
        role: h.role === 'assistant' ? 'assistant' : 'user',
        content: [{
          type: 'text',
          text: h.content,
          // Mark last history message with cache_control for incremental caching
          cache_control: isLastHistoryMessage ? createCacheControl('5m') : undefined,
        }],
      });
    });
    
    // Add the new message (no cache_control as it's new content)
    messages.push({
      role: 'user',
      content: [{
        type: 'text',
        text: newMessage,
      }],
    });
    
    // Use the messages array format for conversation
    const { maxTokens = 4096, temperature = 0.7 } = {};
    
    // Try proxy first
    if (isAIProxyAvailable()) {
      try {
        const response = await callAIProxy('anthropic' as any, 'messages', {
          model: this.model,
          max_tokens: maxTokens,
          temperature,
          system: systemBlocks,
          messages: messages,
        });

        if (response?.content?.[0]?.text) {
          return response.content[0].text;
        }
        
        if (response) {
          console.warn('Claude proxy returned unexpected format:', response);
          throw new Error(`AI Proxy returned unexpected format for Claude: ${JSON.stringify(response)}`);
        }
      } catch (error) {
        console.error('Claude proxy call failed:', error);
        if (isAIProxyAvailable()) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          if (errorMessage.includes('invalid x-api-key') || errorMessage.includes('401')) {
            throw new Error('Anthropic API key is invalid or expired. Please update it using: npx wrangler pages secret put ANTHROPIC_API_KEY');
          }
          throw new Error(`AI proxy failed: ${errorMessage}. Please check Workers secrets and function deployment.`);
        }
      }
    }

    if (!isAIProxyAvailable()) {
      throw new Error('AI proxy not configured. Set VITE_AI_WORKER_URL and Workers secrets.');
    }

    throw new Error('AI proxy returned an empty response.');
  }

  async generateSocialMediaDrafts(projectContext: string, companyInfo: string): Promise<SocialMediaDraft[]> {
    const systemPrompt = `You are a social media manager for a construction/industrial contracting company. Generate professional social media content.`;
    const userMessage = `Company: ${companyInfo}

Project Context:
${projectContext}

Generate social media posts for LinkedIn and Facebook. Return a JSON array with:
- platform: "LINKEDIN" or "FACEBOOK"
- content: The post text (LinkedIn: professional/B2B tone, Facebook: community-focused/engaging tone)
- hashtags: Array of relevant hashtags
- imageSuggestion: Suggested image description

LinkedIn should be professional, technical, B2B-focused. Facebook should be community-focused, slightly casual, engaging.`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async generateRFQDrafts(quote: Quote, materials: MaterialLine[]): Promise<RFQDraft[]> {
    const systemPrompt = `You are a procurement specialist. Draft RFQ emails.`;
    const userMessage = `Generate RFQs for materials: ${materials.map(m => m.description).join(', ')}\nProject: ${quote.projectName}\n\nRespond with JSON array.`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async generateProposalContent(quote: Quote, settings: ProposalSettings): Promise<string> {
    const systemPrompt = `You are a proposal writer for construction projects. Write in ${settings.tone} tone.`;
    const userMessage = `Write proposal for:\nClient: ${quote.clientName}\nProject: ${quote.projectName}\nValue: $${quote.totalPrice}\nScope: ${quote.bidItems.map(i => i.title).join(', ')}\n\nLength: ${settings.length}, Focus: ${settings.focus}`;
    return this.callClaude(systemPrompt, userMessage);
  }

  async generateInvoiceDescription(notes: string[], style: 'BRIEF' | 'BULLET' | 'DETAILED'): Promise<string> {
    const systemPrompt = `You are a professional construction billing assistant. Summarize work logs into a customer-facing invoice description.`;
    const userMessage = `STYLE: ${style}\n\nWORK LOGS:\n${notes.join('\n')}\n\nReturn only the description text.`;
    return this.callClaude(systemPrompt, userMessage);
  }

  async generateOutreach(customer: Customer, companyInfo: CompanyInfo, recentWins: string[]): Promise<OutreachDraft[]> {
    const systemPrompt = `You are a business development specialist. Write concise outreach drafts for construction clients.`;
    const userMessage = `Company: ${companyInfo.name}\nCustomer: ${customer.companyName || customer.contactName}\nRecent wins: ${recentWins.join(', ') || 'None'}\n\nReturn JSON array of drafts: [{ "recipientName": "", "method": "EMAIL", "subject": "", "content": "" }]`;
    const result = await this.callClaudeJSON(systemPrompt, userMessage);
    return Array.isArray(result) ? result : [];
  }

  async optimizeValueProp(companyInfo: CompanyInfo, targetIndustry: string): Promise<string> {
    const systemPrompt = `You are a marketing strategist. Refine value propositions for construction businesses.`;
    const userMessage = `Company: ${companyInfo.name}\nCurrent value prop: ${companyInfo.valueProp || 'N/A'}\nTarget industry: ${targetIndustry}\n\nReturn a single optimized value proposition sentence.`;
    return this.callClaude(systemPrompt, userMessage);
  }

  async scoreLead(customer: Customer, companyInfo: CompanyInfo): Promise<LeadScore> {
    const systemPrompt = `You are a lead qualification specialist. Score this lead 0-100 with reasoning.`;
    const userMessage = `Company: ${companyInfo.name}\nLead: ${customer.companyName || customer.contactName}\nIndustry: ${customer.industry || 'Unknown'}\nNotes: ${customer.notes || 'None'}\n\nReturn JSON: { "score": 75, "reasoning": "..." }`;
    const result = await this.callClaudeJSON<{ score?: number; reasoning?: string | string[] }>(systemPrompt, userMessage);
    const reasoning = Array.isArray(result?.reasoning) ? result.reasoning.join(' ') : (result?.reasoning || '');
    return {
      score: typeof result?.score === 'number' ? result.score : 0,
      reasoning,
      lastUpdated: new Date().toISOString(),
    };
  }

  async analyzeFinancialHealth(projects: { id: string; name: string; budgetHours: number; actualHours: number }[]): Promise<ProjectFinancialHealth[]> {
    const systemPrompt = `You are a project financial analyst. Assess burn rate and health.`;
    const userMessage = `Projects:\n${projects.map(p => `${p.name} (budget: ${p.budgetHours}h, actual: ${p.actualHours}h)`).join('\n')}\n\nReturn JSON array with fields: projectId, projectName, budgetHours, actualHours, burnRate, status, aiAdvice.`;
    const result = await this.callClaudeJSON(systemPrompt, userMessage);
    return Array.isArray(result) ? result : [];
  }

  async generateCollectionEmails(invoices: Invoice[], customers: Customer[], companyInfo: CompanyInfo): Promise<OutreachDraft[]> {
    const systemPrompt = `You are an accounts receivable specialist. Draft polite collection emails.`;
    const userMessage = `Company: ${companyInfo.name}\nInvoices: ${invoices.map(i => `${i.invoiceNumber || i.number}: $${i.total || i.amount}`).join(', ')}\nCustomers: ${customers.map(c => c.companyName).join(', ')}\n\nReturn JSON array of outreach drafts.`;
    const result = await this.callClaudeJSON(systemPrompt, userMessage);
    return Array.isArray(result) ? result : [];
  }

  async generateOutreachEmail(customer: Customer, purpose: string, context: string): Promise<OutreachDraft> {
    const systemPrompt = `You are a business development specialist. Write professional outreach emails.`;
    const userMessage = `Write ${purpose} email for ${customer.name}.\nContext: ${context}\n\nRespond with JSON: { "subject": "", "body": "", "callToAction": "" }`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async generateCustomerInsights(customer: Customer, quotes: Quote[], context: string): Promise<any> {
    const systemPrompt = `You are a customer success specialist. Analyze customer relationships.`;
    const userMessage = `Analyze customer: ${customer.name}\nQuotes: ${quotes.map(q => `${q.projectName}: $${q.totalPrice} (${q.status})`).join(', ')}\n\nRespond with JSON insights.`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async analyzeLeadQuality(customer: Customer, companyInfo: CompanyInfo): Promise<LeadScore> {
    const systemPrompt = `You are a lead qualification specialist. Score this lead.`;
    const userMessage = `Score lead: ${customer.name}\nType: ${customer.type}\nIndustry: ${customer.industry || 'Unknown'}\n\nRespond with JSON: { "score": 75, "factors": [], "recommendations": [] }`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async generateCaseStudy(quote: Quote, companyInfo: CompanyInfo): Promise<CaseStudy> {
    const systemPrompt = `You are a marketing writer. Create compelling case studies.`;
    const userMessage = `Write case study for:\nProject: ${quote.projectName}\nClient: ${quote.clientName}\nCompany: ${companyInfo.name}\nValue: $${quote.totalPrice}\n\nRespond with JSON case study.`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async calculateProjectHealth(quote: Quote): Promise<ProjectFinancialHealth> {
    const systemPrompt = `You are a project financial analyst. Assess project health.`;
    const userMessage = `Assess financial health:\nProject: ${quote.projectName}\nBudget: $${quote.totalPrice}\nHours: ${quote.totalManHours}\n\nRespond with JSON health assessment.`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async generateInvoiceEmailDraft(invoice: Invoice, customer: Customer): Promise<{ subject: string; body: string }> {
    const systemPrompt = `You are a business communication specialist. Write professional invoice emails.`;
    const userMessage = `Write invoice email:\nCustomer: ${customer.name}\nInvoice: ${invoice.invoiceNumber}\nAmount: $${invoice.total}\n\nRespond with JSON: { "subject": "", "body": "" }`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async generatePaymentReminderDraft(invoice: Invoice, customer: Customer, reminderNumber: number): Promise<{ subject: string; body: string }> {
    const systemPrompt = `You are a collections specialist. Write appropriate payment reminders.`;
    const userMessage = `Write reminder #${reminderNumber} for:\nCustomer: ${customer.name}\nInvoice: ${invoice.invoiceNumber}\nAmount: $${invoice.total}\nDue: ${invoice.dueDate}\n\nRespond with JSON.`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async analyzeMaintenanceLog(logText: string, checklists: MaintenanceChecklist[]): Promise<Partial<ServiceReport>> {
    const systemPrompt = `You are a maintenance log analyst. Extract key fields for a service report.`;
    const checklistContext = checklists.map(c => `Checklist: ${c.name} (${c.items?.length || 0} items)`).join('\n');
    const userMessage = `Log Text:\n${logText}\n\nAvailable Checklists:\n${checklistContext}\n\nReturn JSON with fields from ServiceReport: summary, checklistResults, issuesFound, recommendations.`;
    const result = await this.callClaudeJSON(systemPrompt, userMessage);
    return result && typeof result === 'object' ? result : {};
  }

  async generateMaintenanceChecklist(equipmentType: string, lastServiceDate: string): Promise<MaintenanceChecklist> {
    const systemPrompt = `You are a maintenance planning specialist. Create comprehensive checklists.`;
    const userMessage = `Create maintenance checklist for ${equipmentType}\nLast service: ${lastServiceDate}\n\nRespond with JSON checklist.`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }

  async generateServiceReport(workPerformed: string, findings: string): Promise<ServiceReport> {
    const systemPrompt = `You are a service documentation specialist. Create professional reports.`;
    const userMessage = `Create service report:\nWork: ${workPerformed}\nFindings: ${findings}\n\nRespond with JSON report.`;
    return this.callClaudeJSON(systemPrompt, userMessage);
  }
}

// Export singleton instance
let instance: AnthropicProvider | null = null;

export function getAnthropicProvider(): AnthropicProvider {
  if (!instance) {
    instance = new AnthropicProvider();
  }
  return instance;
}

export default AnthropicProvider;
